import { createSelector } from 'reselect';
import { jobsStateType } from './jobs.reducer';
import { JobType } from './jobs.types';
import { resolveMeterType } from '../../helper/MeterHelper';
import { hasMultipleValues } from '../../helper/GroupHelper';
import { searchSelectors } from '../search';
import { applicationSelectors } from '../application';
import { compareBySortSettings } from '../../helper/Sorting';

const getSubState = (state: any) => state.jobs as jobsStateType;

const isLoadingBlocked = createSelector(getSubState, (state) => state.blocked);

/** Ob mindestens ein Ladedurchlauf über alle Logins abgeschlossen ist (#336). */
const hasLoadedJobs = createSelector(getSubState, (state) => state.loaded);

const getJobIds = createSelector(getSubState, (state) => state.jobs);
const getJobEntities = createSelector(getSubState, (state) => state.entities);
const getJobEntitiesMap = createSelector(getJobEntities, (entities) => entities.jobs);

// Enrichment (adds jobInfo.meterType) must preserve identity for unchanged jobs.
// getJobs is upstream of every list read; a fresh object per job on every store
// update would defeat React.memo on 8000 list items. The cache keys on the raw
// entity reference, which is referentially stable except for the mutated job on
// SEND_METER_TASK_RESULT_DONE.
const enrichedJobCache = new WeakMap<object, JobType>();
const enrichJobForRead = (entity: JobType): JobType => {
    if (!entity) return entity;
    const cached = enrichedJobCache.get(entity);
    if (cached) return cached;
    const enriched = {
        ...entity,
        jobInfo: {
            meterType: resolveMeterType(entity?.meter),
        },
    } as JobType;
    enrichedJobCache.set(entity, enriched);
    return enriched;
};

// jobSchema has no nested relations, so a direct entity lookup is equivalent
// to normalizr's denormalize here — and lets the per-entity cache above work,
// which normalizr's denormalize (fresh objects each call) would defeat.
const getJobs = createSelector(getJobIds, getJobEntitiesMap, (ids, jobsById) =>
    (ids || []).map((id) => enrichJobForRead(jobsById[id])).filter((j): j is JobType => !!j),
);

const getPreselectedJobId = createSelector(getJobIds, applicationSelectors.isPreselectJobIfSingleEnabled, (jobIds, isPreselectJobIfSingleEnabled) => {
    if (isPreselectJobIfSingleEnabled && jobIds?.length === 1) {
        return jobIds[0];
    } else {
        return undefined;
    }
});

const getJobsCount = createSelector(getJobIds, (jobIds) => jobIds?.length || 0);

const getJobsDone = createSelector(getJobs, (jobs) => jobs.filter((j) => j.done));
const getCountJobsDone = createSelector(getJobsDone, (jobs) => jobs.length);

const getJobsOpen = createSelector(getJobs, (jobs) => jobs.filter((j) => !j.done));
const getCountJobsOpen = createSelector(getJobsOpen, (jobs) => jobs?.length || 0);

// True when an open job other than the current one exists. The current job is
// excluded explicitly: while offline it stays "open" (done is only set by the
// server response), so counting open jobs alone would always include it.
export const hasOtherOpenJob = (openJobs: Array<{ id: string }> | undefined, currentJobId: string): boolean =>
    (openJobs || []).some((job) => job.id !== currentJobId);

const getHasOtherOpenJobs = (jobId: string) => createSelector(getJobsOpen, (jobs) => hasOtherOpenJob(jobs, jobId));

const getJobById = (jobId: string) =>
    createSelector(getJobEntitiesMap, (jobsById) => enrichJobForRead(jobsById[jobId]));

const isLoading = createSelector(getSubState, (state) => state.loading && state.jobs.length === 0);

const hasJobs = createSelector(getJobIds, (ids) => ids.length > 0);

const getSortSettings = createSelector(getSubState, applicationSelectors.getDefaultJobSortSettings, (state, defaultSortSettings) => {
    return state.sortSettings ?? defaultSortSettings;
});

const getSortedJobs = createSelector(
    getJobs,
    getSortSettings,
    applicationSelectors.getDefaultJobSortSettings,
    (jobs, sortSettings, defaultSortSettings) => {
        const list = jobs ? [...jobs] : [];


        const activeSortSettings = sortSettings ?? defaultSortSettings;

        try {
            if (!activeSortSettings || !activeSortSettings.fields?.length) {
                // Fallback wie bisher
                return list.sort((a, b) =>
                    a.meter?.meterNumber && b.meter?.meterNumber
                        ? a.meter.meterNumber.localeCompare(b.meter.meterNumber)
                        : 0
                );
            }

            return list.sort((a, b) => compareBySortSettings(a, b, activeSortSettings));
        } catch (error) {
            console.log("error", error);
            return list.sort((a, b) =>
                a.meter?.meterNumber && b.meter?.meterNumber
                    ? a.meter.meterNumber.localeCompare(b.meter.meterNumber)
                    : 0
            );
        }
    }
);

const getFilteredJobs = createSelector(getSortedJobs, searchSelectors.getSearchArray, (jobs, searchArray) =>
    jobs?.filter((job) => {
        if (job.utils?.searchString && searchArray && searchArray.length > 0) {
            return searchArray.every((s) => !!job.utils?.searchString?.toUpperCase()?.includes(s?.toUpperCase()));
        }

        return false;
    }),
);

// Pre-split at the selector level so consumers don't have to filter in render.
// Previously JobList kept two useState mirrors and refiltered in an effect on
// every render, which caused an extra pass over all 8000 jobs per store update.
const getFilteredJobsOpen = createSelector(getFilteredJobs, (jobs) => (jobs || []).filter((j) => !j.done));
const getFilteredJobsDone = createSelector(getFilteredJobs, (jobs) => (jobs || []).filter((j) => j.done));

// Combined memoised flags used by every list item. Pulled up from
// JobListItemWrapper, where a per-item selector factory multiplied the cost by
// the number of list items on every store notification. Deliberately not a
// factory: a factory returns a fresh selector with an empty cache per call.
const getJobListMultipleValueFlags = createSelector(getJobs, (jobs) => ({
    hasObjectKeyMultipleValues: hasMultipleValues(jobs, 'meter.meterAddress.objectKey'),
    hasLocationDescriptionMultipleValues: hasMultipleValues(jobs, 'meter.meterAddress.locationDescription'),
}));

const getJobsByIds = (ids: string[]) =>
    createSelector(getJobs, (jobs) => {
        return jobs.filter((job) => ids.includes(job.id));
    });

const getJobsByAccountId = (accountId: string) => createSelector(getJobs, (jobs) => jobs.filter((j) => j.login === accountId));

const getJobIdsByAccountId = (accountId: string) => createSelector(getJobs, (jobs) => jobs.filter((j) => j.login === accountId).map((job) => job.id));

const getShareableJobsByAccountId = (accountId: string) =>
    createSelector(getJobsByAccountId(accountId), (jobs) => jobs.filter((job) => job.jobPermissions?.reShare));

export const isAnyJobReshareAllowed = createSelector(getJobs, applicationSelectors.isGlobalReshareAllowed, (jobs, globalReshareAllowed) => {
    const hasAnyJobConfigAnOverrideForReshareAllowed = jobs?.some((j) => !!j.config?.features?.reshareAllowed);

    if (hasAnyJobConfigAnOverrideForReshareAllowed) {
        return true;
    }

    return globalReshareAllowed;
});

/*
 * Die CRM-Anmeldung wird für den ganzen Account ausgesetzt, sobald ein Auftrag sie verbietet
 * (planung-stories#879). Eine Auftragsliste, in der die Anmeldung an einem Auftrag angeboten wird
 * und am nächsten nicht, wäre nicht erklärbar — und die Registrierung selbst gilt ohnehin dem
 * CRM-Kunden, nicht dem einzelnen Auftrag.
 *
 * `false` ist ein Veto, `true` ausdrücklich kein Ja: EAST liefert das Flag nur mit `false`, ein
 * `true` entsteht allein durch manuelles Aufheben einer Sperre und darf die Mandanten-Einstellung
 * nicht überschreiben. Das ist der Unterschied zu reshareAllowed, wo `true` einschaltet.
 *
 * Der strikte === false-Vergleich ist Absicht: `!j.config?.features?.crmRegistrationEnabled`
 * würde jeden Auftrag ohne Flag zum Veto machen.
 */
export const resolveCrmRegistrationAllowed = (
    jobs: Array<{ config?: { features?: { crmRegistrationEnabled?: boolean } } }> | undefined,
    globalRegistrationAllowed: boolean,
) => {
    const isVetoedByAnyJob = (jobs || []).some((j) => j.config?.features?.crmRegistrationEnabled === false);

    if (isVetoedByAnyJob) {
        return false;
    }

    return globalRegistrationAllowed;
};

const isCRMRegistrationAllowedForAccount = createSelector(
    getJobs,
    applicationSelectors.isCRMRegistrationAllowed,
    (jobs, globalRegistrationAllowed) => resolveCrmRegistrationAllowed(jobs, globalRegistrationAllowed),
);

const hasShareableJobs = createSelector(getJobs, (jobs) => jobs.filter((j) => j.jobPermissions?.reShare).length > 0);
const hasShareableJobsByAccountId = (accountId: string) =>
    createSelector(getShareableJobsByAccountId(accountId), (jobs) => jobs.filter((j) => j.jobPermissions?.reShare).length > 0);

const isSending = createSelector(getSubState, (state) => state.sending);

export default {
    isLoading,
    isLoadingBlocked,
    hasLoadedJobs,
    hasJobs,
    getJobs,
    getJobsCount,
    getJobById,
    getPreselectedJobId,

    getCountJobsDone,
    getCountJobsOpen,
    getHasOtherOpenJobs,

    getJobIdsByAccountId,

    hasShareableJobs,
    hasShareableJobsByAccountId,
    isAnyJobReshareAllowed,
    isCRMRegistrationAllowedForAccount,

    getFilteredJobs,
    getFilteredJobsOpen,
    getFilteredJobsDone,
    getJobListMultipleValueFlags,

    getJobsDone,

    getJobsByIds,
    getJobsByAccountId,
    getShareableJobsByAccountId,
    isSending,

    getSortSettings,
} as const;
